import React, { Fragment, useEffect, useState } from "react";

import { ShapeSelectorIconConfig } from "orion-components/SharedComponents";
import LineStringEditor from "./LineEditor/LineStringEditor";
import PointEditor from "./PointEditor/PointEditor";
import PolygonEditor from "./PolygonEditor/PolygonEditor";
import RectangleEditor from "./RectangleEditor/RectangleEditor";
import CircleEditor from "./CircleEditor/CircleEditor";
import { setMapTools } from "orion-components/Map/Tools/Actions";
import { useDispatch, useSelector } from "react-redux";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import { selectShape } from "orion-components/GlobalData/Actions";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation } from "orion-components/i18n";
import { clearShapes } from "../../../GlobalData/Shapes/actions";

const selectEditorComponent = (shape) => {
	switch (shape) {
		case "Point":
			return <PointEditor />;
		case "LineString":
			return <LineStringEditor />;
		case "Polygon":
			return <PolygonEditor />;
		case "Rectangle":
			return <RectangleEditor />;
		case "Circle":
			return <CircleEditor />;
		default:
			return null;
	}
};

const ShapeSelector = () => {
	const shapes = [
		{
			name: "Point",
			label: getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.point"),
			drawShape: "draw_point",
			iconName: "point",
			width: 44,
			height: 61,
			altName: "point"
		},
		{
			name: "LineString",
			label: getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.linePath"),
			drawShape: "draw_line_string",
			iconName: "line",
			width: 49,
			height: 61,
			altName: "lineString"
		},
		{
			name: "Polygon",
			label: getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.polygon"),
			drawShape: "draw_polygon",
			iconName: "polygon",
			width: 44,
			height: 61,
			altName: "polygon"
		},
		{
			name: "Rectangle",
			label: getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.rectangle"),
			drawShape: "draw_rectangle",
			iconName: "rectangle",
			width: 44,
			height: 61,
			altName: "rectangle"
		},
		{
			name: "Circle",
			label: getTranslation("global.sharedComponents.shapeEditor.components.shapeSelector.circle"),
			drawShape: "draw_circle",
			iconName: "circle",
			width: 44,
			height: 61,
			altName: "circle"
		}
	];
	const [selectedShape, setSelectedShape] = useState("Point");
	const shapeType = useSelector((state) => state.mapState.mapTools?.feature?.geometry?.type);
	const shapeProps = useSelector((state) => state.mapState.mapTools.feature);
	const [hoveredName, setHoveredName] = useState("");
	const shapesReducer = useSelector((state) => state.shapes);
	const contextId = useSelector((state) => selectedContextSelector(state)?.entity.id);
	const dir = useSelector((state) => getDir(state));

	const dispatch = useDispatch();

	useEffect(() => {
		if (shapeType !== undefined && shapeType !== selectedShape) {
			if (shapeProps?.properties?.isRectangle && shapesReducer.selectedShape === "Rectangle") {
				setSelectedShape("Rectangle");
			} else if (shapeProps?.properties?.isCircle && shapesReducer.selectedShape === "Circle") {
				setSelectedShape("Circle");
			} else {
				setSelectedShape(shapeType);
			}
		}
	}, [shapeType]);

	const switchShapes = (shape) => {
		dispatch(clearShapes());
		setSelectedShape(shape.name);
		dispatch(selectShape(shape.name));
		dispatch(setMapTools({ type: null, mode: null, feature: null, latLng: null }));
		dispatch(setMapTools({ type: "drawing", mode: shape.drawShape }));
		EventBus.publish("drawChangeMode", shape.drawShape);
	};

	return (
		<Fragment>
			<div
				style={{
					display: "flex",
					marginTop: 32,
					...(dir === "ltr" && { marginLeft: 32.31, marginRight: 32.31 }),
					...(dir === "rtl" && { marginRight: 32.31, marginLeft: 32.31 }),
					height: 61,
					opacity: "1",
					width: 364.69
				}}
			>
				{shapes.map((shape, index) => {
					return (
						<div
							key={index}
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								...(dir === "ltr" && { marginRight: 38 }),
								...(dir === "rtl" && { marginLeft: 38 }),
								width: shape.width,
								height: shape.height
							}}
							onMouseLeave={() => {
								setHoveredName("");
							}}
						>
							<div
								style={{
									height: 44,
									width: 44,
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									display: "flex",
									backgroundColor:
										selectedShape === shape.name
											? "#4DB5F4"
											: hoveredName === shape.name
												? "#32353B"
												: "transparent",
									borderRadius: selectedShape === shape.name ? "5px" : 0,
									opacity: 1,
									cursor: "pointer",
									marginBottom: 6
								}}
								onClick={() => {
									if (shapeProps === null) {
										switchShapes(shape);
									} else {
										if (contextId !== shapeProps.id) {
											switchShapes(shape);
										}
									}
									if (
										shape.name === "LineString" ||
										shape.name === "Polygon" ||
										shape.name === "Rectangle"
									) {
										EventBus.publish("drawUpdateStyles", shapesReducer[shape.altName]);
									}
								}}
								onMouseMoveCapture={() => {
									if (selectedShape !== shape.name && hoveredName !== shape.name) {
										setHoveredName(shape.name);
									}
								}}
							>
								{ShapeSelectorIconConfig(shape.iconName)}
							</div>

							<div
								id="shapeName"
								style={{
									textAlign: "center",
									width: shape.width,
									height: 11,
									fontFamily: "Roboto",
									fontSize: 10,
									letterSpacing: 0,
									color: selectedShape === shape.name ? "#FFFFFF" : "#A7ACB0",
									opacity: "1"
								}}
							>
								{selectedShape === shape.name || hoveredName === shape.name ? shape.label : null}
							</div>
						</div>
					);
				})}
			</div>
			{selectEditorComponent(selectedShape)}
		</Fragment>
	);
};

export default ShapeSelector;
